import { create } from 'zustand';
import type { PolymarketEventDetail } from '@/lib/polymark/polymarketEvents';

export interface MarketRealtimeSnapshotInput {
  marketId: string;
  conditionId?: string | null;
  yesTokenId?: string | null;
  noTokenId?: string | null;
  yesPrice?: string | number | null;
  noPrice?: string | number | null;
  marketClosed?: number | string | boolean | null;
  resolvedOutcome?: string | null;
}

export interface MarketRealtimeData {
  marketId: string;
  conditionId?: string;
  yesTokenId?: string;
  noTokenId?: string;
  yesPrice?: number;
  noPrice?: number;
  bestBid?: number;
  bestAsk?: number;
  lastTradePrice?: number;
  lastUpdateSource?: string;
  marketClosed?: boolean;
  resolvedOutcome?: string | null;
  updatedAt?: number;
}

interface TokenMarketIndex {
  marketId: string;
  outcomeIndex: 0 | 1;
}

interface MarketRealtimeStoreState {
  marketsById: Record<string, MarketRealtimeData>;
  tokenIndex: Record<string, TokenMarketIndex>;
  conditionIndex: Record<string, string>;
  marketDetailsByKey: Record<string, PolymarketEventDetail>;
  setMarketSnapshot: (snapshot: MarketRealtimeSnapshotInput) => void;
  setMarketSnapshots: (snapshots: MarketRealtimeSnapshotInput[]) => void;
  updateTokenPrice: (tokenId: string, price: number, meta?: {
    bestBid?: number;
    bestAsk?: number;
    lastTradePrice?: number;
    lastUpdateSource?: string;
  }) => void;
  markMarketResolved: (payload: {
    marketId?: string;
    conditionId?: string;
    assetId?: string;
    resolvedOutcome?: string | null;
  }) => void;
  setMarketDetailCache: (detail: PolymarketEventDetail, keys: string[]) => void;
}

function normalizeTokenId(value?: string | null): string {
  return String(value ?? '').trim();
}

function normalizeNumber(value?: string | number | null): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;

  const num = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(num) ? num : undefined;
}

function normalizePrice(value: number): number | undefined {
  if (!Number.isFinite(value)) return undefined;
  if (value < 0) return undefined;
  if (value > 1 && value <= 100) return value / 100;
  if (value > 100) return undefined;
  return value;
}

function invertPrice(value: number): number {
  return Math.max(0, Math.min(1, 1 - value));
}

function normalizeCacheKey(value?: string | number | null): string {
  return String(value ?? '').trim();
}

function getCacheKeyNamespaces(keys: string[]): string[] {
  const namespaces = keys
    .map((key) => key.match(/^lang:[^:]+:/)?.[0] ?? '')
    .filter(Boolean);

  return Array.from(new Set(namespaces));
}

function addNamespacedCacheKey(keys: Set<string>, namespaces: string[], key: string): void {
  if (!key) return;
  if (namespaces.length === 0) {
    keys.add(key);
    return;
  }

  namespaces.forEach((namespace) => {
    keys.add(`${namespace}${key}`);
  });
}

function getMarketDetailCacheKeys(detail: PolymarketEventDetail, keys: string[] = []): string[] {
  const normalizedKeys = new Set(keys.map(normalizeCacheKey).filter(Boolean));
  const namespaces = getCacheKeyNamespaces(Array.from(normalizedKeys));
  const eventId = normalizeCacheKey(detail.event?.id);
  const eventSlug = normalizeCacheKey(detail.event?.slug);

  addNamespacedCacheKey(normalizedKeys, namespaces, `event:${eventId}`);
  addNamespacedCacheKey(normalizedKeys, namespaces, `slug:${eventSlug}`);

  detail.markets?.forEach((market) => {
    const marketId = normalizeCacheKey(market.market_id);
    const marketSlug = normalizeCacheKey(market.market_slug);
    const conditionId = normalizeCacheKey(market.condition_id);

    addNamespacedCacheKey(normalizedKeys, namespaces, `market:${marketId}`);
    addNamespacedCacheKey(normalizedKeys, namespaces, `slug:${marketSlug}`);
    addNamespacedCacheKey(normalizedKeys, namespaces, `condition:${conditionId}`);
  });

  return Array.from(normalizedKeys);
}

function applyMarketSnapshots(
  state: MarketRealtimeStoreState,
  snapshots: MarketRealtimeSnapshotInput[],
): Pick<MarketRealtimeStoreState, 'marketsById' | 'tokenIndex' | 'conditionIndex'> | null {
  if (snapshots.length === 0) return null;

  const marketsById = { ...state.marketsById };
  const tokenIndex = { ...state.tokenIndex };
  const conditionIndex = { ...state.conditionIndex };
  let didChange = false;
  const updatedAt = Date.now();

  snapshots.forEach((snapshot) => {
    const marketId = String(snapshot.marketId || '').trim();
    if (!marketId) return;

    const previous = marketsById[marketId];
    const yesTokenId = normalizeTokenId(snapshot.yesTokenId);
    const noTokenId = normalizeTokenId(snapshot.noTokenId);
    const yesPrice = normalizePrice(normalizeNumber(snapshot.yesPrice) ?? NaN);
    const noPrice = normalizePrice(normalizeNumber(snapshot.noPrice) ?? NaN);
    const conditionId = snapshot.conditionId?.trim() || previous?.conditionId;
    const marketClosed =
      snapshot.marketClosed !== undefined && snapshot.marketClosed !== null
        ? snapshot.marketClosed === true || Number(snapshot.marketClosed) === 1 || snapshot.marketClosed === 'true'
        : previous?.marketClosed;
    const nextMarket: MarketRealtimeData = {
      ...previous,
      marketId,
      conditionId,
      yesTokenId: yesTokenId || previous?.yesTokenId,
      noTokenId: noTokenId || previous?.noTokenId,
      yesPrice: yesPrice ?? previous?.yesPrice,
      noPrice: noPrice ?? previous?.noPrice,
      marketClosed,
      resolvedOutcome: snapshot.resolvedOutcome ?? previous?.resolvedOutcome,
      updatedAt: previous?.updatedAt,
    };
    const snapshotChanged =
      !previous ||
      previous.conditionId !== nextMarket.conditionId ||
      previous.yesTokenId !== nextMarket.yesTokenId ||
      previous.noTokenId !== nextMarket.noTokenId ||
      previous.yesPrice !== nextMarket.yesPrice ||
      previous.noPrice !== nextMarket.noPrice ||
      previous.marketClosed !== nextMarket.marketClosed ||
      previous.resolvedOutcome !== nextMarket.resolvedOutcome;

    if (!snapshotChanged) return;

    if (previous?.yesTokenId && yesTokenId && previous.yesTokenId !== yesTokenId) {
      delete tokenIndex[previous.yesTokenId];
    }
    if (previous?.noTokenId && noTokenId && previous.noTokenId !== noTokenId) {
      delete tokenIndex[previous.noTokenId];
    }
    if (previous?.conditionId && conditionId && previous.conditionId !== conditionId) {
      delete conditionIndex[previous.conditionId];
    }

    marketsById[marketId] = {
      ...nextMarket,
      updatedAt,
    };

    if (yesTokenId) tokenIndex[yesTokenId] = { marketId, outcomeIndex: 0 };
    if (noTokenId) tokenIndex[noTokenId] = { marketId, outcomeIndex: 1 };
    if (conditionId) conditionIndex[conditionId] = marketId;
    didChange = true;
  });

  return didChange ? { marketsById, tokenIndex, conditionIndex } : null;
}

export const useMarketRealtimeStore = create<MarketRealtimeStoreState>()((set) => ({
  marketsById: {},
  tokenIndex: {},
  conditionIndex: {},
  marketDetailsByKey: {},
  setMarketSnapshot: (snapshot) => {
    set((state) => applyMarketSnapshots(state, [snapshot]) ?? state);
  },
  setMarketSnapshots: (snapshots) => {
    set((state) => applyMarketSnapshots(state, snapshots) ?? state);
  },
  updateTokenPrice: (tokenId, rawPrice, meta) => {
    const tokenKey = normalizeTokenId(tokenId);
    const price = normalizePrice(rawPrice);
    if (!tokenKey || price === undefined) return;

    set((state) => {
      const index = state.tokenIndex[tokenKey];
      if (!index) return state;

      const current = state.marketsById[index.marketId];
      if (!current) return state;

      const next: MarketRealtimeData = {
        ...current,
        updatedAt: Date.now(),
        bestBid: meta?.bestBid ?? current.bestBid,
        bestAsk: meta?.bestAsk ?? current.bestAsk,
        lastTradePrice: meta?.lastTradePrice ?? current.lastTradePrice,
        lastUpdateSource: meta?.lastUpdateSource ?? current.lastUpdateSource,
      };

      if (index.outcomeIndex === 0) {
        next.yesPrice = price;
        next.noPrice = invertPrice(price);
      } else {
        next.noPrice = price;
        next.yesPrice = invertPrice(price);
      }

      return {
        ...state,
        marketsById: {
          ...state.marketsById,
          [index.marketId]: next,
        },
      };
    });
  },
  markMarketResolved: (payload) => {
    set((state) => {
      const marketId =
        payload.marketId ||
        (payload.assetId ? state.tokenIndex[normalizeTokenId(payload.assetId)]?.marketId : undefined) ||
        (payload.conditionId ? state.conditionIndex[normalizeTokenId(payload.conditionId)] : undefined);

      if (!marketId || !state.marketsById[marketId]) return state;

      return {
        ...state,
        marketsById: {
          ...state.marketsById,
          [marketId]: {
            ...state.marketsById[marketId],
            marketClosed: true,
            resolvedOutcome: payload.resolvedOutcome ?? state.marketsById[marketId].resolvedOutcome,
            updatedAt: Date.now(),
          },
        },
      };
    });
  },
  setMarketDetailCache: (detail, keys) => {
    const cacheKeys = getMarketDetailCacheKeys(detail, keys);
    if (cacheKeys.length === 0) return;

    set((state) => {
      const marketDetailsByKey = { ...state.marketDetailsByKey };
      cacheKeys.forEach((key) => {
        marketDetailsByKey[key] = detail;
      });
      const realtimeSnapshots = detail.markets?.map((market) => ({
        marketId: market.market_id,
        conditionId: market.condition_id,
        yesTokenId: market.clob_tokens_id_yes,
        noTokenId: market.clob_tokens_id_no,
        yesPrice: market.outcome_yes_price,
        noPrice: market.outcome_no_price,
        marketClosed: market.market_closed,
        resolvedOutcome: market.resolved_outcome,
      })) ?? [];
      const realtimeState = applyMarketSnapshots(state, realtimeSnapshots);

      return {
        ...state,
        ...(realtimeState ?? {}),
        marketDetailsByKey,
      };
    });
  },
}));

export function getMarketRealtimeSnapshot(marketId?: string | null): MarketRealtimeData | undefined {
  if (!marketId) return undefined;
  return useMarketRealtimeStore.getState().marketsById[String(marketId)];
}

export function getMarketDetailCache(keys: Array<string | null | undefined>): PolymarketEventDetail | undefined {
  const marketDetailsByKey = useMarketRealtimeStore.getState().marketDetailsByKey;
  for (const key of keys) {
    const normalizedKey = normalizeCacheKey(key);
    if (normalizedKey && marketDetailsByKey[normalizedKey]) {
      return marketDetailsByKey[normalizedKey];
    }
  }
  return undefined;
}

export function setMarketDetailCache(detail: PolymarketEventDetail, keys: string[] = []) {
  useMarketRealtimeStore.getState().setMarketDetailCache(detail, keys);
}
